import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  CogIcon,
  UserGroupIcon,
  UserIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import { toast } from 'react-toastify';
import { getFixedDropdownPosition } from './dropdownPosition';

function DashboardSwitcher() {
  const [isOpen, setIsOpen] = useState(false);
  const [availableDashboards, setAvailableDashboards] = useState([]);
  const [currentDashboard, setCurrentDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const dropdownContentRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, login, isAdmin, isManager, isSuperAdmin } = useAuth();

  // Determine current dashboard from route
  useEffect(() => {
    if (location.pathname === '/' || location.pathname.startsWith('/attendance') || 
        location.pathname.startsWith('/users') || location.pathname.startsWith('/settings')) {
      setCurrentDashboard({ type: 'admin', label: 'Admin Dashboard', icon: CogIcon });
    } else if (location.pathname === '/manager-dashboard') {
      setCurrentDashboard({ type: 'manager', label: 'Manager Dashboard', icon: UserGroupIcon });
    } else if (location.pathname === '/user-dashboard') {
      setCurrentDashboard({ type: 'user', label: 'User Dashboard', icon: UserIcon });
    }
  }, [location.pathname]);

  // Determine available dashboards based on user privileges
  useEffect(() => {
    if (!user) return;

    const dashboards = [];
    const currentType = currentDashboard?.type;

    // Admin Dashboard
    if (isAdmin()) {
      dashboards.push({
        type: 'admin',
        label: 'Admin Dashboard',
        icon: CogIcon,
        path: '/',
        isCurrent: currentType === 'admin'
      });

      // Check if admin has manager privileges (show manager dashboard if they have manager access)
      // For manager-admin role, they definitely have manager access
      // For super admin, check if they have manager privileges in user object
      if (user.role === 'manager-admin' || user.isManager || user.isTeamLead || user.hasSubordinates) {
        dashboards.push({
          type: 'manager',
          label: 'Manager Dashboard',
          icon: UserGroupIcon,
          path: '/manager-dashboard',
          isCurrent: currentType === 'manager'
        });
      }

      // Check if admin has user account (for super admin, they should not have access)
      // Only show if user has an ID (user account exists) and is not super admin
      // Manager-admin users should be able to switch to user dashboard
      if (user.id && !isSuperAdmin()) {
        dashboards.push({
          type: 'user',
          label: 'User Dashboard',
          icon: UserIcon,
          path: '/user-dashboard',
          isCurrent: currentType === 'user',
          requiresSwitch: true
        });
      }
    }
    // Manager Dashboard (user with manager privileges)
    else if (user.role === 'user' && isManager()) {
      dashboards.push({
        type: 'manager',
        label: 'Manager Dashboard',
        icon: UserGroupIcon,
        path: '/manager-dashboard',
        isCurrent: currentType === 'manager'
      });

      dashboards.push({
        type: 'user',
        label: 'User Dashboard',
        icon: UserIcon,
        path: '/user-dashboard',
        isCurrent: currentType === 'user'
      });

      // If user has BOTH manager and team lead access, allow switching to Admin (Super Admin panel)
      // This is a special elevated case, not for all managers.
      if (user.isManager && user.isTeamLead) {
        dashboards.push({
          type: 'admin',
          label: 'Admin Dashboard',
          icon: CogIcon,
          path: '/',
          isCurrent: currentType === 'admin',
          requiresSwitch: true
        });
      }
    }
    // User Dashboard (regular user with manager privileges)
    else if (user.role === 'user') {
      dashboards.push({
        type: 'user',
        label: 'User Dashboard',
        icon: UserIcon,
        path: '/user-dashboard',
        isCurrent: currentType === 'user'
      });

      // Show manager dashboard if user has manager privileges
      if (isManager()) {
        dashboards.push({
          type: 'manager',
          label: 'Manager Dashboard',
          icon: UserGroupIcon,
          path: '/manager-dashboard',
          isCurrent: currentType === 'manager'
        });
      }
    }
    // Manager-Admin role (switched from manager to admin)
    else if (user.role === 'manager-admin') {
      dashboards.push({
        type: 'admin',
        label: 'Admin Dashboard',
        icon: CogIcon,
        path: '/',
        isCurrent: currentType === 'admin'
      });

      dashboards.push({
        type: 'manager',
        label: 'Manager Dashboard',
        icon: UserGroupIcon,
        path: '/manager-dashboard',
        isCurrent: currentType === 'manager'
      });

      dashboards.push({
        type: 'user',
        label: 'User Dashboard',
        icon: UserIcon,
        path: '/user-dashboard',
        isCurrent: currentType === 'user',
        requiresSwitch: true
      });
    }

    setAvailableDashboards(dashboards);
  }, [user, isAdmin, isManager, isSuperAdmin, currentDashboard]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          dropdownContentRef.current && !dropdownContentRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Update dropdown position when it opens or window changes
  useEffect(() => {
    if (!isOpen || !buttonRef.current || !dropdownContentRef.current) return;

    const updatePosition = () => {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdown = dropdownContentRef.current;
      if (!dropdown) return;

      const dropdownRect = dropdown.getBoundingClientRect();
      const { left, top, width, maxHeight } = getFixedDropdownPosition({
        triggerRect: rect,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        preferredWidth: dropdownRect.width || 224,
        dropdownHeight: dropdownRect.height,
      });

      dropdown.style.top = `${top}px`;
      dropdown.style.left = `${left}px`;
      dropdown.style.right = 'auto';
      dropdown.style.width = `${width}px`;
      dropdown.style.maxHeight = `${Math.max(maxHeight, 0)}px`;
    };

    updatePosition();
    
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen]);

  const handleDashboardSwitch = async (dashboard) => {
    if (dashboard.isCurrent) {
      setIsOpen(false);
      return;
    }

    setLoading(true);
    setIsOpen(false);

    try {
      // If switching requires a role change, call the appropriate API
      if (dashboard.requiresSwitch) {
        if (dashboard.type === 'admin' && user.role === 'user') {
          // Switch from user to admin
          const response = await authAPI.switchToAdmin(user.email);
          login(response.data.token, response.data.user);
          navigate(dashboard.path, { replace: true });
        } else if (dashboard.type === 'user' && (isAdmin() || user.role === 'manager-admin')) {
          // Switch from admin/manager-admin to user
          const response = await authAPI.switchToUser(user.email);
          login(response.data.token, response.data.user);
          window.location.href = dashboard.path; // Use hard navigation for user dashboard
        } else {
          // Direct navigation for other cases
          navigate(dashboard.path, { replace: true });
        }
      } else {
        // Manager-admin must switch back to the user token before opening manager dashboard.
        if (dashboard.type === 'manager' && user.role === 'manager-admin') {
          const response = await authAPI.switchToUser(user.email);
          login(response.data.token, response.data.user);
          navigate(dashboard.path, { replace: true });
        }
        // Direct navigation (same role, different dashboard)
        if (dashboard.type === 'user') {
          window.location.href = dashboard.path;
        } else if (!(dashboard.type === 'manager' && user.role === 'manager-admin')) {
          navigate(dashboard.path, { replace: true });
        }
      }
    } catch (error) {
      console.error('Failed to switch dashboard:', error);
      toast.error(error.response?.data?.error || 'Failed to switch dashboard');
      setLoading(false);
    }
  };

  // Don't show switcher if user only has access to one dashboard
  if (!availableDashboards || availableDashboards.length <= 1) {
    return null;
  }

  const CurrentIcon = currentDashboard?.icon || CogIcon;

  // Determine button style based on current route (for gradient headers)
  const isGradientHeader = location.pathname === '/manager-dashboard' || location.pathname === '/user-dashboard';
  const buttonClass = isGradientHeader
    ? "inline-flex items-center px-3 lg:px-4 py-2 lg:py-2.5 border border-white/15 text-xs lg:text-sm font-semibold rounded-xl text-white bg-white/5 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
    : "inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed";

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        disabled={loading}
        className={buttonClass}
      >
        <CurrentIcon className="h-4 w-4 lg:h-5 lg:w-5 mr-2" />
        <span className="hidden lg:inline">{currentDashboard?.label || 'Dashboard'}</span>
        <span className="lg:hidden">{currentDashboard?.label?.split(' ')[0] || 'Dashboard'}</span>
        <ChevronDownIcon className="h-4 w-4 ml-2" />
      </button>

      {/* Dropdown - Rendered via Portal to body */}
      {isOpen && typeof document !== 'undefined' && createPortal(
        <div
          ref={dropdownContentRef}
          className="fixed w-56 overflow-y-auto rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5"
          style={{
            zIndex: 99999,
            maxWidth: 'calc(100vw - 16px)'
          }}
        >
          <div className="py-1" role="menu">
            {availableDashboards.map((dashboard) => {
              const DashboardIcon = dashboard.icon;
              return (
                <button
                  key={dashboard.type}
                  onClick={() => handleDashboardSwitch(dashboard)}
                  className={`w-full text-left px-4 py-2 text-sm flex items-center ${
                    dashboard.isCurrent
                      ? 'bg-indigo-50 text-indigo-700 font-medium'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  role="menuitem"
                >
                  <DashboardIcon className="h-5 w-5 mr-3" />
                  <span>{dashboard.label}</span>
                  {dashboard.isCurrent && (
                    <span className="ml-auto text-indigo-600">✓</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default DashboardSwitcher;
